import React, { useEffect, useRef, useState } from 'react';
import { apiService } from '../../services/api.service';
import { useAuth } from '../../contexts/AuthContext';

type DepositStatus = 'pending' | 'pending_admin' | 'processing' | 'success' | 'expired' | 'failed' | 'cancelled';

const QR_EXPIRY_MS = 10 * 60 * 1000;
const formatDepositInput = (value: string) => value.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const REPORTABLE_BANKS = [
    'ACB',
    'Agribank',
    'BIDV',
    'BVBank',
    'Eximbank',
    'HDBank',
    'HSBC',
    'LPBank',
    'MB Bank',
    'MSB',
    'Nam A Bank',
    'OCB',
    'Sacombank',
    'SeABank',
    'SHB',
    'Techcombank',
    'TPBank',
    'VIB',
    'Vietcombank',
    'VietABank',
    'VietinBank',
    'VPBank',
    'MoMo',
    'ZaloPay',
    'Khác',
];

interface DepositTransaction {
    transactionId: string;
    type: 'deposit' | 'payment' | 'withdraw' | 'refund' | 'bonus';
    amount: number;
    status: DepositStatus;
    createdAt?: string;
    expiredAt?: string;
    completedAt?: string;
    referenceCode?: string;
    bonusPercent?: number;
    bonusAmount?: number;
    creditedAmount?: number;
    bankCode?: string;
    bankAccount?: string;
    bankAccountName?: string;
    reportedTransferAmount?: number;
    reportedSenderAccountName?: string;
    reportedSenderAccountNumber?: string;
    reportedSenderBankName?: string;
    qrCode?: string;
    qrData?: string;
}

interface DepositResponse {
    success: boolean;
    transaction: DepositTransaction;
    qrCode: string;
    bankInfo: {
        bankCode: string;
        bankAccount: string;
        bankAccountName: string;
        amount: number;
        content: string;
    };
    instructions?: { step1: string; step2: string; step3: string };
}

interface WalletInfo {
    balance: number;
}

const formatAmount = (amount: number) => `${amount.toLocaleString('vi-VN')} đ`;
const getExpiryTimestamp = (transaction: DepositTransaction) => transaction.expiredAt
    ? new Date(transaction.expiredAt).getTime()
    : transaction.status === 'pending_admin'
        ? Number.POSITIVE_INFINITY
        : transaction.createdAt
        ? new Date(transaction.createdAt).getTime() + QR_EXPIRY_MS
        : 0;
const formatCountdown = (seconds: number) => `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;

const formatDate = (value?: string) => value
    ? new Intl.DateTimeFormat('vi-VN', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
    : '-';

const statusLabel: Record<DepositStatus, string> = {
    pending: 'Đang chờ chuyển khoản',
    pending_admin: 'Chờ admin duyệt',
    processing: 'Đang xử lý',
    success: 'Thành công',
    expired: 'Hết hạn',
    failed: 'Thất bại',
    cancelled: 'Đã hủy',
};

const DepositPage: React.FC = () => {
    const { user } = useAuth();
    const isPartner = user?.role === 'partner';
    const [amount, setAmount] = useState('0');
    const amountInputRef = useRef<HTMLInputElement>(null);
    const [deposit, setDeposit] = useState<DepositResponse | null>(null);
    const [history, setHistory] = useState<DepositTransaction[]>([]);
    const [walletBalance, setWalletBalance] = useState(0);
    const [outstandingAmount, setOutstandingAmount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
    const [isRequestingAdmin, setIsRequestingAdmin] = useState(false);
    const [requestingAdminTransactionId, setRequestingAdminTransactionId] = useState<string | null>(null);
    const [reportingTransaction, setReportingTransaction] = useState<DepositTransaction | null>(null);
    const [reportedTransferAmount, setReportedTransferAmount] = useState('');
    const [reportedSenderAccountName, setReportedSenderAccountName] = useState('');
    const [reportedSenderAccountNumber, setReportedSenderAccountNumber] = useState('');
    const [reportedSenderBankName, setReportedSenderBankName] = useState('');
    const [reportedOtherBankName, setReportedOtherBankName] = useState('');
    const [reportFormError, setReportFormError] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [maintenanceEnabled, setMaintenanceEnabled] = useState<boolean | null>(null);
    const [maintenanceStatusError, setMaintenanceStatusError] = useState('');
    const [now, setNow] = useState(Date.now());
    const pendingStatuses: DepositStatus[] = ['pending', 'pending_admin', 'processing'];
    const activePendingTransaction = [deposit?.transaction, ...history].find((transaction) =>
        transaction !== undefined
        && pendingStatuses.includes(transaction.status)
        && getExpiryTimestamp(transaction) > now,
    );
    const secondsRemaining = activePendingTransaction
        ? Math.max(0, Math.ceil((getExpiryTimestamp(activePendingTransaction) - now) / 1000))
        : 0;
    const currentQrExpired = Boolean(
        deposit
        && pendingStatuses.includes(deposit.transaction.status)
        && getExpiryTimestamp(deposit.transaction) <= now,
    );
    const canCancelCurrentQr = Boolean(
        deposit
        && ['pending', 'pending_admin'].includes(deposit.transaction.status)
        && !currentQrExpired,
    );

    const loadData = async () => {
        try {
            const [wallet, transactions, orderSummary] = await Promise.all([
                apiService.get<WalletInfo>('/transactions/wallet'),
                apiService.get<{ data: DepositTransaction[] }>('/transactions/history?limit=20&offset=0'),
                isPartner
                    ? apiService.get<{ unpaidTotal: number }>('/orders/summary')
                    : Promise.resolve({ unpaidTotal: 0 }),
            ]);
            setWalletBalance(wallet.balance || 0);
            setOutstandingAmount(orderSummary.unpaidTotal || 0);
            setHistory((transactions.data || []).filter((item) => item.status !== 'success' || true));
        } catch {
            setError('Không tải được thông tin ví và lịch sử nạp tiền.');
        }
    };

    useEffect(() => {
        void loadData();
    }, [isPartner]);

    useEffect(() => {
        apiService.get<{ enabled: boolean }>('/system-settings/maintenance')
            .then((status) => {
                setMaintenanceEnabled(status.enabled);
                setMaintenanceStatusError('');
            })
            .catch((requestError) => {
                console.error('Không tải được trạng thái bảo trì:', requestError);
                setMaintenanceEnabled(null);
                setMaintenanceStatusError('Không tải được trạng thái bảo trì. Tạm thời không thể tạo yêu cầu nạp tiền.');
            });
    }, []);

    useEffect(() => {
        if (!activePendingTransaction) return;
        const interval = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(interval);
    }, [activePendingTransaction?.transactionId]);

    useEffect(() => {
        const transactionId = deposit?.transaction.transactionId;
        if (!transactionId || !['pending', 'pending_admin', 'processing'].includes(deposit.transaction.status)) return;

        let checking = false;
        const refreshTransactionStatus = async () => {
            if (checking) return;
            if (getExpiryTimestamp(deposit.transaction) <= Date.now()) {
                setDeposit((current) => current?.transaction.transactionId === transactionId
                    ? { ...current, transaction: { ...current.transaction, status: 'expired' } }
                    : current);
                return;
            }
            checking = true;
            try {
                const response = await apiService.get<{ data: DepositTransaction[] }>('/transactions/history?limit=20&offset=0');
                const transactions = response.data || [];
                setHistory(transactions);
                const latest = transactions.find((item) => item.transactionId === transactionId);
                if (latest) {
                    setDeposit((current) => current?.transaction.transactionId === transactionId
                        ? { ...current, transaction: latest }
                        : current);
                    if (latest.status === 'success') {
                        setMessage('Giao dịch đã được xác nhận thành công.');
                        setError('');
                        if (deposit.transaction.type === 'deposit') {
                            const wallet = await apiService.get<WalletInfo>('/transactions/wallet');
                            setWalletBalance(wallet.balance || 0);
                        }
                    }
                }
            } catch {
                // Keep the pending screen usable while status refresh is temporarily unavailable.
            } finally {
                checking = false;
            }
        };

        void refreshTransactionStatus();
        const interval = window.setInterval(() => void refreshTransactionStatus(), 5000);
        return () => window.clearInterval(interval);
    }, [deposit?.transaction.transactionId, deposit?.transaction.status, deposit?.transaction.type, deposit?.transaction.expiredAt]);

    const handleCreateDeposit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError('');
        setMessage('');
        if (maintenanceEnabled !== false) {
            setError(maintenanceEnabled
                ? 'Hệ thống đang bảo trì, tạm thời không thể nạp tiền.'
                : maintenanceStatusError || 'Chưa xác định được trạng thái hệ thống, vui lòng thử lại.');
            return;
        }
        const numericAmount = Number(amount);
        if (!Number.isFinite(numericAmount) || numericAmount < 5000) {
            setError('Số tiền nạp tối thiểu là 5.000 đ.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await apiService.post<DepositResponse>('/transactions/deposit', {
                amount: numericAmount,
                paymentMethod: 'bank_transfer',
            });
            setDeposit(response);
            setMessage('QR đã được tạo. Khách vui lòng quét mã QR hoặc chuyển khoản (ghi đúng NỘI DUNG CHUYỂN KHOẢN ở phía dưới để duyệt tự động).');
            await loadData();
        } catch (requestError: any) {
            const responseMessage = requestError.response?.data?.message;
            setError(Array.isArray(responseMessage) ? responseMessage.join(', ') : responseMessage || 'Không tạo được yêu cầu nạp tiền.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelDeposit = async () => {
        if (!deposit || !canCancelCurrentQr) return;

        const transactionId = deposit.transaction.transactionId;
        setIsLoading(true);
        setError('');
        try {
            const response = await apiService.post<{ message: string; transaction: DepositTransaction }>(
                '/transactions/deposit/cancel',
                { transactionId },
            );
            setDeposit((current) => current?.transaction.transactionId === transactionId
                ? { ...current, transaction: response.transaction }
                : current);
            setHistory((current) => current.map((item) => item.transactionId === transactionId
                ? response.transaction
                : item));
            setShowCancelConfirmation(false);
            setMessage(response.message || 'Đã hủy mã QR.');
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không thể hủy mã QR. Giao dịch có thể đang được xử lý.');
        } finally {
            setIsLoading(false);
        }
    };

    const openDepositReport = (transaction: DepositTransaction) => {
        const transactionExpired = transaction.status === 'expired'
            || (transaction.status === 'pending' && getExpiryTimestamp(transaction) <= Date.now());
        if (transaction.type !== 'deposit' || !transactionExpired) return;

        setReportingTransaction(transaction);
        setReportedTransferAmount(String(transaction.amount));
        setReportedSenderAccountName('');
        setReportedSenderAccountNumber('');
        setReportedSenderBankName('');
        setReportedOtherBankName('');
        setReportFormError('');
        setError('');
        setMessage('');
    };

    const handleRequestAdminApproval = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!reportingTransaction) return;
        const amount = Number(reportedTransferAmount);
        const senderAccountName = reportedSenderAccountName.trim();
        const senderAccountNumber = reportedSenderAccountNumber.trim();
        const senderBankName = (reportedSenderBankName === 'Khác' ? reportedOtherBankName : reportedSenderBankName).trim();
        if (!Number.isSafeInteger(amount) || amount <= 0) {
            setReportFormError('Vui lòng nhập số tiền đã chuyển hợp lệ.');
            return;
        }
        if (!senderAccountName || !senderAccountNumber || !senderBankName) {
            setReportFormError('Vui lòng nhập đầy đủ tên, số tài khoản và ngân hàng đã chuyển.');
            return;
        }

        const transactionId = reportingTransaction.transactionId;
        setRequestingAdminTransactionId(transactionId);
        setIsRequestingAdmin(true);
        setReportFormError('');
        setMessage('');
        try {
            const response = await apiService.post<{ message: string; transaction: DepositTransaction }>(
                '/transactions/deposit/request-admin',
                {
                    transactionId,
                    reportedTransferAmount: amount,
                    reportedSenderAccountName: senderAccountName,
                    reportedSenderAccountNumber: senderAccountNumber,
                    reportedSenderBankName: senderBankName,
                },
            );
            setDeposit((current) => current?.transaction.transactionId === transactionId
                ? { ...current, transaction: response.transaction }
                : current);
            setHistory((current) => current.map((item) => item.transactionId === transactionId
                ? response.transaction
                : item));
            setMessage(response.message || 'Đã gửi yêu cầu admin kiểm tra giao dịch.');
            setReportingTransaction(null);
        } catch (requestError: any) {
            setReportFormError(requestError.response?.data?.message || 'Không thể gửi yêu cầu admin duyệt.');
        } finally {
            setIsRequestingAdmin(false);
            setRequestingAdminTransactionId(null);
        }
    };

    const handleCreateOutstandingPayment = async () => {
        if (outstandingAmount <= 0) return;
        setIsLoading(true);
        setError('');
        setMessage('');
        try {
            const response = await apiService.post<DepositResponse>('/transactions/deposit', {
                amount: outstandingAmount,
                paymentMethod: 'bank_transfer',
                payAll: true,
            });
            setDeposit(response);
            setMessage('QR thanh toán tất cả order chưa thanh toán đã được tạo.');
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không tạo được yêu cầu thanh toán.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewPendingTransaction = (transaction: DepositTransaction) => {
        if (getExpiryTimestamp(transaction) <= Date.now()) {
            setError('Mã QR đã hết hạn. Vui lòng tạo mã mới.');
            return;
        }
        if (!transaction.qrCode || !transaction.bankAccount || !transaction.bankAccountName) {
            setError('Giao dịch này không còn thông tin QR để hiển thị lại.');
            return;
        }

        setDeposit({
            success: true,
            transaction,
            qrCode: transaction.qrCode,
            bankInfo: {
                bankCode: transaction.bankCode || '-',
                bankAccount: transaction.bankAccount,
                bankAccountName: transaction.bankAccountName,
                amount: transaction.amount,
                content: transaction.transactionId,
            },
        });
        setMessage('Đã mở lại QR của giao dịch đang chờ.');
        setError('');
    };

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50 p-4 md:p-8">
            {maintenanceEnabled !== false && (
                <div role="status" className={`mx-auto mb-5 max-w-5xl rounded-xl px-4 py-3 text-sm ${maintenanceEnabled ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700'}`}>
                    {maintenanceEnabled
                        ? 'Hệ thống đang bảo trì. Tạm thời không thể tạo yêu cầu nạp tiền.'
                        : maintenanceStatusError || 'Đang kiểm tra trạng thái hệ thống; chưa thể tạo yêu cầu nạp tiền.'}
                </div>
            )}
            {reportingTransaction && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <form
                        onSubmit={handleRequestAdminApproval}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="deposit-report-title"
                        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
                    >
                        <h2 id="deposit-report-title" className="text-lg font-semibold text-gray-900">Báo đã chuyển khoản</h2>
                        <p className="mt-1 text-sm text-gray-500">
                            Nhập thông tin giao dịch để admin kiểm tra mã {reportingTransaction.transactionId}.
                        </p>
                        <label htmlFor="reported-transfer-amount" className="mt-4 block text-sm font-medium text-gray-700">Số tiền đã chuyển</label>
                        <input
                            id="reported-transfer-amount"
                            type="number"
                            min="1"
                            step="1"
                            required
                            value={reportedTransferAmount}
                            onChange={(event) => setReportedTransferAmount(event.target.value)}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                            placeholder="Nhập số tiền thực chuyển"
                        />
                        <label htmlFor="reported-sender-name" className="mt-4 block text-sm font-medium text-gray-700">Tên chủ tài khoản chuyển</label>
                        <input
                            id="reported-sender-name"
                            type="text"
                            maxLength={100}
                            required
                            value={reportedSenderAccountName}
                            onChange={(event) => setReportedSenderAccountName(event.target.value)}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                            placeholder="Tên chủ tài khoản ngân hàng"
                        />
                        <label htmlFor="reported-sender-account-number" className="mt-4 block text-sm font-medium text-gray-700">Số tài khoản đã chuyển</label>
                        <input
                            id="reported-sender-account-number"
                            type="text"
                            maxLength={50}
                            required
                            value={reportedSenderAccountNumber}
                            onChange={(event) => setReportedSenderAccountNumber(event.target.value)}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                            placeholder="Nhập số tài khoản ngân hàng gửi"
                        />
                        <label htmlFor="reported-sender-bank" className="mt-4 block text-sm font-medium text-gray-700">Tên ngân hàng đã chuyển</label>
                        <select
                            id="reported-sender-bank"
                            required
                            value={reportedSenderBankName}
                            onChange={(event) => {
                                setReportedSenderBankName(event.target.value);
                                setReportedOtherBankName('');
                            }}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        >
                            <option value="">Chọn ngân hàng</option>
                            {REPORTABLE_BANKS.map((bank) => <option key={bank} value={bank}>{bank}</option>)}
                        </select>
                        {reportedSenderBankName === 'Khác' && (
                            <input
                                type="text"
                                aria-label="Nhập tên ngân hàng khác"
                                maxLength={100}
                                required
                                value={reportedOtherBankName}
                                onChange={(event) => setReportedOtherBankName(event.target.value)}
                                className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                placeholder="Nhập tên ngân hàng"
                            />
                        )}
                        {reportFormError && <p role="alert" className="mt-3 text-sm text-red-600">{reportFormError}</p>}
                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setReportingTransaction(null)}
                                disabled={isRequestingAdmin}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Hủy
                            </button>
                            <button
                                type="submit"
                                disabled={isRequestingAdmin}
                                className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50"
                            >
                                {isRequestingAdmin ? 'Đang gửi...' : 'Gửi admin kiểm tra'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
            <div className="mx-auto max-w-5xl space-y-5">
                <div className="grid gap-4 md:grid-cols-[1fr_240px]">
                    <section className="rounded-2xl bg-white p-5 shadow-sm">
                        <p className="text-sm font-medium text-blue-600">Nạp tiền qua ngân hàng</p>
                        <h2 className="mt-1 text-2xl font-semibold text-gray-900">Nhập số tiền muốn nạp</h2>
                        <form onSubmit={handleCreateDeposit} className="mt-5 flex flex-col gap-3 sm:flex-row">
                            <div className="flex-1">
                                <input
                                    ref={amountInputRef}
                                    value={formatDepositInput(amount)}
                                    onChange={(event) => {
                                        const input = event.target;
                                        const digitsBeforeCaret = input.value
                                            .slice(0, input.selectionStart ?? input.value.length)
                                            .replace(/\D/g, '').length;
                                        const nextAmount = input.value.replace(/\D/g, '');
                                        setAmount(nextAmount);
                                        window.requestAnimationFrame(() => {
                                            const formattedAmount = formatDepositInput(nextAmount);
                                            let caretPosition = 0;
                                            let digitsSeen = 0;
                                            while (caretPosition < formattedAmount.length && digitsSeen < digitsBeforeCaret) {
                                                if (/\d/.test(formattedAmount[caretPosition])) digitsSeen += 1;
                                                caretPosition += 1;
                                            }
                                            amountInputRef.current?.setSelectionRange(caretPosition, caretPosition);
                                        });
                                    }}
                                    disabled={maintenanceEnabled !== false}
                                    inputMode="numeric"
                                    aria-label="Số tiền nạp"
                                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-lg outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                    placeholder="Nhập số tiền"
                                />
                                <p className="mt-1 text-xs text-gray-500">Số tiền nạp tối thiểu là 5.000 đ.</p>
                            </div>
                            <button
                                type="submit"
                                disabled={maintenanceEnabled !== false || isLoading || Boolean(activePendingTransaction)}
                                className="rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isLoading ? 'Đang xử lý...' : activePendingTransaction ? 'Đang có QR hiệu lực' : 'Tạo QR'}
                            </button>
                        </form>
                        {activePendingTransaction && <p className="mt-3 text-sm text-amber-700">Mã QR hiện tại còn hiệu lực {formatCountdown(secondsRemaining)}. Hết hạn mới có thể tạo mã mới.</p>}
                    </section>
                    <section className="rounded-2xl bg-slate-900 p-5 text-white shadow-sm">
                        <p className="text-sm text-slate-300">Số tiền hiện tại</p>
                        <p className="mt-2 text-3xl font-semibold">{formatAmount(walletBalance)}</p>
                    </section>
                </div>

                {isPartner && outstandingAmount > 0 && (
                    <section className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-orange-100 bg-orange-50 p-5 shadow-sm">
                        <div>
                            <p className="text-sm text-orange-700">Tổng mã chung sức chưa thanh toán</p>
                            <p className="mt-1 text-2xl font-semibold text-orange-800">{formatAmount(outstandingAmount)}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => void handleCreateOutstandingPayment()}
                            disabled={isLoading || Boolean(activePendingTransaction)}
                            className="rounded-xl bg-orange-500 px-5 py-3 font-semibold text-white hover:bg-orange-600 disabled:opacity-50"
                        >
                            {isLoading ? 'Đang tạo QR...' : activePendingTransaction ? 'Đang có QR hiệu lực' : 'Thanh toán tất cả'}
                        </button>
                    </section>
                )}

                {(message || error) && (
                    <div className={`rounded-xl px-4 py-3 text-sm ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                        {error || message}
                    </div>
                )}

                {deposit && (
                    <section className="grid gap-6 rounded-2xl bg-white p-5 shadow-sm md:grid-cols-[220px_1fr]">
                        <div className="flex items-start justify-center">
                            {currentQrExpired
                                ? <div className="flex h-52 w-52 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm font-medium text-gray-500">QR đã hết hạn</div>
                                : <img src={deposit.qrCode} alt="QR chuyển khoản nạp tiền" className="h-52 w-52 rounded-lg border border-gray-100" />}
                        </div>
                        <div>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <h3 className="text-lg font-semibold text-gray-900">Thông tin chuyển khoản</h3>
                                <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800">
                                    {statusLabel[currentQrExpired ? 'expired' : deposit.transaction.status]}
                                </span>
                            </div>
                            <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                                <div><dt className="text-gray-500">Ngân hàng</dt><dd className="font-semibold">{deposit.bankInfo.bankCode}</dd></div>
                                <div><dt className="text-gray-500">Số tài khoản</dt><dd className="font-semibold">{deposit.bankInfo.bankAccount}</dd></div>
                                <div><dt className="text-gray-500">Chủ tài khoản</dt><dd className="font-semibold">{deposit.bankInfo.bankAccountName}</dd></div>
                                <div><dt className="text-gray-500">Số tiền</dt><dd className="font-semibold text-blue-600">{formatAmount(deposit.bankInfo.amount)}</dd></div>
                            </dl>
                            <p className="mt-4 rounded-lg bg-gray-50 p-3 text-sm">Nội dung: <strong>{deposit.bankInfo.content}</strong></p>
                            {deposit.transaction.type === 'deposit' && (deposit.transaction.bonusAmount || 0) > 0 && (
                                <p className="mt-3 rounded-lg bg-green-50 p-3 text-sm text-green-800">
                                    Tiền nạp: <strong>{formatAmount(deposit.transaction.amount)}</strong> · Cộng thêm: <strong>{formatAmount(deposit.transaction.bonusAmount || 0)}</strong> · Tổng vào ví: <strong>{formatAmount(deposit.transaction.creditedAmount || deposit.transaction.amount)}</strong>
                                </p>
                            )}
                            {activePendingTransaction?.transactionId === deposit.transaction.transactionId && <p className="mt-4 text-sm text-amber-700">Tự kiểm tra giao dịch mỗi 5 giây. QR còn hiệu lực {formatCountdown(secondsRemaining)}.</p>}
                            {currentQrExpired && <p className="mt-4 text-sm text-red-600">QR đã hết hạn. Bạn có thể tạo mã mới.</p>}
                            {currentQrExpired && deposit.transaction.status === 'expired' && (
                                <button
                                    type="button"
                                    onClick={() => openDepositReport(deposit.transaction)}
                                    disabled={isRequestingAdmin || isLoading}
                                    className="mt-4 rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                                >
                                    {isRequestingAdmin ? 'Đang gửi yêu cầu...' : 'Báo đã chuyển khoản'}
                                </button>
                            )}
                            {deposit.transaction.status === 'cancelled' && <p className="mt-4 text-sm text-gray-600">QR đã được hủy. Bạn có thể tạo mã mới.</p>}
                            {canCancelCurrentQr && (
                                <button
                                    type="button"
                                    onClick={() => setShowCancelConfirmation(true)}
                                    disabled={isLoading}
                                    className="mt-4 rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                                >
                                    Hủy QR
                                </button>
                            )}
                        </div>
                    </section>
                )}

                {showCancelConfirmation && deposit && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                        <section
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="cancel-qr-title"
                            className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
                        >
                            <h3 id="cancel-qr-title" className="text-lg font-semibold text-gray-900">Hủy mã QR?</h3>
                            <p className="mt-2 text-sm text-gray-600">Bạn có chắc muốn hủy mã QR {deposit.transaction.transactionId} không? Mã này sẽ không thể dùng để thanh toán nữa.</p>
                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowCancelConfirmation(false)}
                                    disabled={isLoading}
                                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    Giữ mã QR
                                </button>
                                <button
                                    type="button"
                                    onClick={() => void handleCancelDeposit()}
                                    disabled={isLoading}
                                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                                >
                                    {isLoading ? 'Đang hủy...' : 'Xác nhận hủy'}
                                </button>
                            </div>
                        </section>
                    </div>
                )}

                <section className="rounded-2xl bg-white p-5 shadow-sm">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Lịch sử nạp tiền</h3>
                        <button type="button" onClick={() => void loadData()} className="text-sm font-medium text-blue-600 hover:text-blue-700">Làm mới</button>
                    </div>
                    <div className="mt-4 divide-y divide-gray-100">
                        {history.length === 0 ? <p className="py-6 text-sm text-gray-500">Chưa có giao dịch nạp tiền.</p> : history.map((item) => {
                            const canViewQr = pendingStatuses.includes(item.status) && getExpiryTimestamp(item) > now && Boolean(item.qrCode);
                            const isExpired = item.status === 'expired'
                                || (item.status === 'pending' && getExpiryTimestamp(item) <= now);
                            const canReportDeposit = item.type === 'deposit' && isExpired;
                            const displayedStatus = isExpired ? 'expired' : item.status;
                            return (
                                <div key={item.transactionId} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
                                    <div>
                                        <p className="font-semibold text-gray-800">{formatAmount(item.amount)}</p>
                                        <p className="text-xs text-gray-400">{item.transactionId} · {formatDate(item.createdAt)}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${displayedStatus === 'success' ? 'bg-green-100 text-green-700' : displayedStatus === 'expired' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                            {statusLabel[displayedStatus] || displayedStatus}
                                        </span>
                                        {canReportDeposit && (
                                            <button
                                                type="button"
                                                onClick={() => openDepositReport(item)}
                                                disabled={requestingAdminTransactionId === item.transactionId}
                                                className="font-medium text-orange-600 hover:text-orange-700 disabled:opacity-50"
                                            >
                                                {requestingAdminTransactionId === item.transactionId ? 'Đang gửi...' : 'Báo đã chuyển khoản'}
                                            </button>
                                        )}
                                        {canViewQr && (
                                            <button
                                                type="button"
                                                onClick={() => handleViewPendingTransaction(item)}
                                                className="font-medium text-blue-600 hover:text-blue-700"
                                            >
                                                Xem QR
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default DepositPage;
